import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";

interface HeaderProps {
  toggleUserMenu: () => void;
}

export function Header({ toggleUserMenu }: HeaderProps) {
  const location = useLocation();
  const { user } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const isActive = (path: string) => {
    return location.pathname === path
      ? "text-[#3fb950]"
      : "text-gray-300 hover:text-white";
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  return (
    <header className="border-b border-[#30363d] bg-[#161b22] sticky top-0 z-50">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and navigation */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3">
              <img
                src="https://voltblocks.com/wp-content/uploads/2024/12/Volt-Blocks-Trans-White-font.png"
                alt="Volt Blocks"
                className="h-8"
              />
              <span className="text-white font-medium">Pattern Generator</span>
            </Link>
            <nav className="hidden md:ml-10 md:flex md:space-x-8">
              <Link
                to="/templates"
                className={`${isActive(
                  "/templates"
                )} px-3 py-2 text-sm font-medium`}
              >
                Templates
              </Link>
              <Link
                to="/generator"
                className={`${isActive(
                  "/generator"
                )} px-3 py-2 text-sm font-medium`}
              >
                Generator
              </Link>
              <Link
                to="/pricing"
                className={`${isActive(
                  "/pricing"
                )} px-3 py-2 text-sm font-medium`}
              >
                Pricing
              </Link>
              <Link
                to="/contact"
                className={`${isActive(
                  "/contact"
                )} px-3 py-2 text-sm font-medium`}
              >
                Contact
              </Link>
              {user && (
                <Link
                  to="/dashboard"
                  className={`${isActive(
                    "/dashboard"
                  )} px-3 py-2 text-sm font-medium`}
                >
                  Dashboard
                </Link>
              )}
            </nav>
          </div>

          {/* User menu and mobile menu button */}
          <div className="flex items-center">
            {!user ? (
              <Link
                to="/auth?mode=signin"
                className="ml-4 px-4 py-2 rounded-lg bg-[#238636] text-white hover:bg-[#2ea043] transition-colors"
              >
                Sign In
              </Link>
            ) : (
              <div className="flex items-center">
                <span className="text-gray-400 text-sm mr-2 hidden md:block">
                  {user.email}
                </span>
              </div>
            )}

            <button
              onClick={toggleMobileMenu}
              className="ml-4 md:hidden p-2 rounded-lg text-gray-400 hover:text-white hover:bg-[#1f6feb]/10"
            >
              <span className="sr-only">Open menu</span>
              {mobileMenuOpen ? (
                <X className="h-6 w-6" />
              ) : (
                <Menu className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-[#30363d]">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            <Link
              to="/templates"
              className={`${
                isActive("/templates")
                  ? "bg-[#1f6feb]/20 text-[#58a6ff]"
                  : "text-gray-300 hover:bg-[#1f6feb]/10 hover:text-white"
              } block px-3 py-2 rounded-lg text-base font-medium`}
              onClick={toggleMobileMenu}
            >
              Templates
            </Link>
            <Link
              to="/generator"
              className={`${
                isActive("/generator")
                  ? "bg-[#1f6feb]/20 text-[#58a6ff]"
                  : "text-gray-300 hover:bg-[#1f6feb]/10 hover:text-white"
              } block px-3 py-2 rounded-lg text-base font-medium`}
              onClick={toggleMobileMenu}
            >
              Generator
            </Link>
            <Link
              to="/pricing"
              className={`${
                isActive("/pricing")
                  ? "bg-[#1f6feb]/20 text-[#58a6ff]"
                  : "text-gray-300 hover:bg-[#1f6feb]/10 hover:text-white"
              } block px-3 py-2 rounded-lg text-base font-medium`}
              onClick={toggleMobileMenu}
            >
              Pricing
            </Link>
            <Link
              to="/contact"
              className={`${
                isActive("/contact")
                  ? "bg-[#1f6feb]/20 text-[#58a6ff]"
                  : "text-gray-300 hover:bg-[#1f6feb]/10 hover:text-white"
              } block px-3 py-2 rounded-lg text-base font-medium`}
              onClick={toggleMobileMenu}
            >
              Contact
            </Link>
            {user && (
              <>
                <Link
                  to="/dashboard"
                  className={`${
                    isActive("/dashboard")
                      ? "bg-[#1f6feb]/20 text-[#58a6ff]"
                      : "text-gray-300 hover:bg-[#1f6feb]/10 hover:text-white"
                  } block px-3 py-2 rounded-lg text-base font-medium`}
                  onClick={toggleMobileMenu}
                >
                  Dashboard
                </Link>
                <div className="pt-4 pb-3 border-t border-[#30363d]">
                  <div className="flex items-center px-3">
                    <div className="flex-shrink-0">
                      <div className="h-10 w-10 rounded-full bg-[#1f6feb]/20 flex items-center justify-center">
                        <User className="h-5 w-5 text-[#58a6ff]" />
                      </div>
                    </div>
                    <div className="ml-3">
                      <div className="text-base font-medium text-white">
                        {user.email}
                      </div>
                      <div className="text-sm font-medium text-gray-400">
                        Free Trial
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
